import { datadogRum } from '@newfront-insurance/dd-rum';

import type { UIMessageWithMetadata } from './types';

export type NovaTurnTelemetryPath = 'durable' | 'non_durable';
export type NovaTurnTelemetryOutcome =
  | 'cancelled'
  | 'completed_without_text'
  | 'error'
  | 'superseded'
  | 'view_replaced'
  | 'visible_text';

const ACTION_NAME = 'nova.turn.time_to_first_visible_text';

type PendingTurn = {
  actionKey: string;
  baselineAssistantText: Map<string, string>;
};

const pendingTurns = new Map<string, PendingTurn>();
let actionSequence = 0;

export function startNovaTurnTelemetry({
  chatUuid,
  messages,
  path,
  trigger,
}: {
  chatUuid: string;
  messages: readonly UIMessageWithMetadata[];
  path: NovaTurnTelemetryPath;
  trigger?: 'regenerate-message' | 'submit-message';
}): void {
  const normalizedChatUuid = normalizeChatUuid(chatUuid);
  const previous = pendingTurns.get(normalizedChatUuid);
  if (previous) {
    stopPendingTurn(normalizedChatUuid, previous, 'superseded');
  }

  const actionKey = `nova-turn-${++actionSequence}`;
  const turnKind =
    trigger === 'regenerate-message'
      ? 'regeneration'
      : messages.at(-1)?.role === 'assistant'
        ? 'continuation'
        : 'message';
  datadogRum.startAction(ACTION_NAME, {
    actionKey,
    context: {
      path,
      turn_kind: turnKind,
    },
  });
  pendingTurns.set(normalizedChatUuid, {
    actionKey,
    baselineAssistantText: collectAssistantText(messages),
  });
}

export function observeNovaTurnText({
  chatUuid,
  durablePreviewMessages,
  messages,
}: {
  chatUuid: string;
  durablePreviewMessages: readonly UIMessageWithMetadata[];
  messages: readonly UIMessageWithMetadata[];
}): void {
  const normalizedChatUuid = normalizeChatUuid(chatUuid);
  const pending = pendingTurns.get(normalizedChatUuid);
  if (!pending) {
    return;
  }

  if (durablePreviewMessages.some(hasVisibleAssistantText)) {
    stopPendingTurn(normalizedChatUuid, pending, 'visible_text', 'durable_preview');
    return;
  }

  const changedAssistantMessage = messages.find((message) => {
    if (!hasVisibleAssistantText(message)) {
      return false;
    }

    return pending.baselineAssistantText.get(message.id) !== getMessageText(message);
  });

  if (changedAssistantMessage) {
    stopPendingTurn(normalizedChatUuid, pending, 'visible_text', 'canonical');
  }
}

export function settleNovaTurnTelemetry(chatUuid: string, outcome: Exclude<NovaTurnTelemetryOutcome, 'visible_text'>) {
  const normalizedChatUuid = normalizeChatUuid(chatUuid);
  const pending = pendingTurns.get(normalizedChatUuid);
  if (pending) {
    stopPendingTurn(normalizedChatUuid, pending, outcome);
  }
}

export function settleNovaTurnTelemetryForViewChange(previousChatUuid: string, nextChatUuid: string): void {
  if (normalizeChatUuid(previousChatUuid) !== normalizeChatUuid(nextChatUuid)) {
    settleNovaTurnTelemetry(previousChatUuid, 'view_replaced');
  }
}

function collectAssistantText(messages: readonly UIMessageWithMetadata[]): Map<string, string> {
  return new Map(
    messages.filter((message) => message.role === 'assistant').map((message) => [message.id, getMessageText(message)]),
  );
}

function getMessageText(message: UIMessageWithMetadata): string {
  return message.parts
    .filter((part): part is Extract<typeof part, { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('');
}

function hasVisibleAssistantText(message: UIMessageWithMetadata): boolean {
  return message.role === 'assistant' && getMessageText(message).trim().length > 0;
}

function stopPendingTurn(
  chatUuid: string,
  pending: PendingTurn,
  outcome: NovaTurnTelemetryOutcome,
  source?: 'canonical' | 'durable_preview',
): void {
  pendingTurns.delete(chatUuid);
  datadogRum.stopAction(ACTION_NAME, {
    actionKey: pending.actionKey,
    context: {
      outcome,
      ...(source ? { source } : {}),
    },
  });
}

function normalizeChatUuid(chatUuid: string): string {
  return chatUuid.replace(/^new-chat-/, '');
}

export function resetNovaTurnTelemetryForTests(): void {
  pendingTurns.clear();
  actionSequence = 0;
}
